import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  ArrowLeft,
  RefreshCw, 
  Plus,
  X,
  Image,
  Edit2,
  Save,
  Trash2,
  Home,
  Wrench,
  RefreshCcw,
  Droplets,
  Upload,
  Camera
} from 'lucide-react';
import { toast } from 'sonner';

const SERVICE_CONFIG = {
  'metal-roof-installation': { 
    label: 'Metal Roof Installation', 
    icon: Home,
    color: 'bg-blue-500'
  },
  'roof-repairs': { 
    label: 'Roof Repairs', 
    icon: Wrench,
    color: 'bg-orange-500'
  },
  'roof-replacement': { 
    label: 'Roof Replacement', 
    icon: RefreshCcw,
    color: 'bg-green-500'
  },
  'gutter-installation': { 
    label: 'Gutter Installation', 
    icon: Droplets,
    color: 'bg-purple-500'
  }
};

const PhotoCard = ({ photo, onDelete, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [label, setLabel] = useState(photo.label || '');
  const [suburb, setSuburb] = useState(photo.suburb || '');
  const [isDeleting, setIsDeleting] = useState(false);

  const handleSave = async () => {
    await onUpdate(photo._id, { label, suburb });
    setIsEditing(false);
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    await onDelete(photo._id);
  };

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden group relative">
      <div className="aspect-[4/3] relative">
        <img 
          src={photo.url} 
          alt={photo.label || 'Project photo'}
          className="w-full h-full object-cover"
        />
        {/* Delete button overlay */}
        <button
          onClick={handleDelete}
          disabled={isDeleting}
          className="absolute top-2 right-2 w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
        >
          {isDeleting ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
        </button>
      </div>
      
      <div className="p-3">
        {isEditing ? (
          <div className="space-y-2">
            <Input
              placeholder="Label (e.g., New roof installation)"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="text-sm"
            />
            <Input
              placeholder="Suburb (e.g., Cecil Hills)"
              value={suburb}
              onChange={(e) => setSuburb(e.target.value)}
              className="text-sm"
            />
            <div className="flex gap-2">
              <Button size="sm" onClick={handleSave} className="flex-1">
                <Save className="w-3 h-3 mr-1" /> Save
              </Button>
              <Button size="sm" variant="outline" onClick={() => setIsEditing(false)}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <p className="font-medium text-gray-900 text-sm truncate">
              {photo.label || 'No label'}
            </p>
            {photo.suburb && (
              <p className="text-gray-500 text-xs">{photo.suburb}</p>
            )}
            <button
              onClick={() => setIsEditing(true)}
              className="text-primary text-xs flex items-center mt-1 hover:underline"
            >
              <Edit2 className="w-3 h-3 mr-1" /> Edit
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const AddPhotoModal = ({ service, onClose, onAdd, onUpload }) => {
  const [url, setUrl] = useState('');
  const [label, setLabel] = useState('');
  const [suburb, setSuburb] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadPreview, setUploadPreview] = useState(null);
  const fileInputRef = React.useRef(null);
  const cameraInputRef = React.useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB.');
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onload = (e) => setUploadPreview(e.target.result);
    reader.readAsDataURL(file);

    // Upload file
    setIsUploading(true);
    const uploadedUrl = await onUpload(file);
    setIsUploading(false);
    
    if (uploadedUrl) {
      setUrl(uploadedUrl);
    }
  };

  const handleAdd = async () => {
    if (!url) {
      toast.error('Please select a photo or enter a URL');
      return;
    }
    
    setIsAdding(true);
    await onAdd({ service, url, label, suburb });
    setIsAdding(false);
    onClose();
  };

  const config = SERVICE_CONFIG[service];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold">Add Photo to {config.label}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          {/* File Upload Section */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload from Device
            </label>
            {/* Photo Library Input (no capture attribute) */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept="image/*"
              className="hidden"
              data-testid="photo-library-input"
            />
            {/* Camera Input (with capture attribute) */}
            <input
              type="file"
              ref={cameraInputRef}
              onChange={handleFileSelect}
              accept="image/*"
              capture="environment"
              className="hidden"
              data-testid="camera-input"
            />
            
            {uploadPreview ? (
              <div className="border-2 border-green-300 bg-green-50 rounded-lg p-4 text-center">
                <img src={uploadPreview} alt="Preview" className="w-32 h-32 object-cover rounded-lg mx-auto mb-2" />
                <p className="text-sm text-green-600 font-medium">Photo ready!</p>
                <button
                  onClick={() => {
                    setUploadPreview(null);
                    setUrl('');
                  }}
                  className="text-xs text-gray-500 hover:text-red-500 mt-1"
                >
                  Remove and choose another
                </button>
              </div>
            ) : isUploading ? (
              <div className="border-2 border-dashed border-primary rounded-lg p-6 text-center bg-primary/5">
                <RefreshCw className="w-8 h-8 text-primary animate-spin mx-auto mb-2" />
                <p className="text-sm text-gray-600">Uploading photo...</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                  className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-primary hover:bg-primary/5 transition-colors"
                  data-testid="select-from-library-btn"
                >
                  <Image className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm font-medium text-gray-700">Photo Library</p>
                  <p className="text-xs text-gray-400">Choose existing</p>
                </button>
                <button
                  onClick={() => cameraInputRef.current?.click()}
                  disabled={isUploading}
                  className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-primary hover:bg-primary/5 transition-colors"
                  data-testid="take-photo-btn"
                >
                  <Camera className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm font-medium text-gray-700">Take Photo</p>
                  <p className="text-xs text-gray-400">Use camera</p>
                </button>
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">or paste URL</span>
            </div>
          </div>

          {/* URL Input */}
          <div>
            <Input
              placeholder="https://example.com/photo.jpg"
              value={url}
              onChange={(e) => {
                setUrl(e.target.value);
                setUploadPreview(null);
              }}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Label
            </label>
            <Input
              placeholder="e.g., Complete roof replacement"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Suburb
            </label>
            <Input
              placeholder="e.g., Cecil Hills"
              value={suburb}
              onChange={(e) => setSuburb(e.target.value)}
            />
          </div>

          {/* Preview from URL */}
          {url && !uploadPreview && (
            <div className="border rounded-lg p-2">
              <p className="text-xs text-gray-500 mb-2">Preview:</p>
              <img 
                src={url} 
                alt="Preview" 
                className="w-full h-32 object-cover rounded"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <Button
              onClick={handleAdd}
              disabled={isAdding || isUploading || !url}
              className="flex-1 bg-primary"
            >
              {isAdding ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Adding...</>
              ) : (
                <><Plus className="w-4 h-4 mr-2" /> Add Photo</>
              )}
            </Button>
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const AdminPhotosPage = () => {
  const [photos, setPhotos] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [activeService, setActiveService] = useState('metal-roof-installation');
  const [showAddModal, setShowAddModal] = useState(false);
  const navigate = useNavigate();

  const fetchPhotos = useCallback(async () => {
    const token = sessionStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/photos`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 401) {
        sessionStorage.removeItem('adminToken');
        navigate('/admin');
        return;
      }

      const data = await response.json();
      if (data.success) {
        setPhotos(data.photos);
      }
    } catch (error) {
      toast.error('Failed to fetch photos');
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchPhotos();
  }, [fetchPhotos]);

  const handleAddPhoto = async (photoData) => {
    const token = sessionStorage.getItem('adminToken');
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/photos`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(photoData)
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Photo added successfully');
        fetchPhotos();
      } else {
        toast.error('Failed to add photo');
      }
    } catch (error) {
      toast.error('Failed to add photo');
    }
  };

  const handleUploadFile = async (file) => {
    const token = sessionStorage.getItem('adminToken');
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();
      if (data.success) {
        // Return the full URL
        return `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      } else {
        toast.error('Failed to upload photo');
        return null;
      }
    } catch (error) {
      toast.error('Failed to upload photo');
      return null;
    }
  };

  const handleUpdatePhoto = async (photoId, updateData) => {
    const token = sessionStorage.getItem('adminToken');
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/photos/${photoId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updateData)
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Photo updated');
        fetchPhotos();
      } else {
        toast.error('Failed to update photo');
      }
    } catch (error) {
      toast.error('Failed to update photo');
    }
  };

  const handleDeletePhoto = async (photoId) => {
    const token = sessionStorage.getItem('adminToken');
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/photos/${photoId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Photo deleted');
        fetchPhotos();
      } else {
        toast.error('Failed to delete photo');
      }
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  const currentPhotos = photos[activeService] || [];
  const config = SERVICE_CONFIG[activeService];
  const Icon = config?.icon;

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="text-gray-500 hover:text-gray-700"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-xl font-bold text-gray-900">Photo Management</h1>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchPhotos}
            disabled={isLoading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Service Tabs */}
        <div className="bg-white rounded-xl p-4 shadow-sm mb-6">
          <div className="flex flex-wrap gap-2">
            {Object.entries(SERVICE_CONFIG).map(([key, cfg]) => {
              const TabIcon = cfg.icon;
              const count = (photos[key] || []).length;
              return (
                <button
                  key={key}
                  onClick={() => setActiveService(key)}
                  className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                    activeService === key
                      ? `${cfg.color} text-white`
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <TabIcon className="w-4 h-4 mr-2" />
                  {cfg.label}
                  <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                    activeService === key ? 'bg-white/20' : 'bg-gray-200'
                  }`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Current Service Section */}
        <div className="bg-white rounded-xl p-6 shadow-sm">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center">
              {Icon && (
                <div className={`w-10 h-10 ${config.color} rounded-lg flex items-center justify-center mr-3`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
              )}
              <div>
                <h2 className="text-lg font-bold text-gray-900">{config?.label}</h2>
                <p className="text-sm text-gray-500">{currentPhotos.length} photos</p>
              </div>
            </div>
            <Button onClick={() => setShowAddModal(true)} className="bg-primary">
              <Plus className="w-4 h-4 mr-2" />
              Add Photo
            </Button>
          </div>

          {isLoading ? (
            <div className="text-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto text-primary" />
              <p className="text-gray-500 mt-4">Loading photos...</p>
            </div>
          ) : currentPhotos.length === 0 ? (
            <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-lg">
              <Image className="w-12 h-12 mx-auto text-gray-300" />
              <p className="text-gray-500 mt-4">No photos yet</p>
              <p className="text-sm text-gray-400">Click &quot;Add Photo&quot; to get started</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {currentPhotos.map((photo) => (
                <PhotoCard
                  key={photo._id}
                  photo={photo}
                  onDelete={handleDeletePhoto}
                  onUpdate={handleUpdatePhoto}
                />
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Add Photo Modal */}
      {showAddModal && (
        <AddPhotoModal
          service={activeService}
          onClose={() => setShowAddModal(false)}
          onAdd={handleAddPhoto}
          onUpload={handleUploadFile}
        />
      )}
    </div>
  );
};

export default AdminPhotosPage;
